/**
 * Pure decision logic for removing traits from a task in the web Edit Task flow.
 *
 * Removing a trait from a task does NOT delete its collected data — the trait's
 * entryCollections are preserved (hidden, since all read paths key off the
 * task's dataCollectionConfigs) and reappear if the trait is re-added. The UI
 * still (a) restricts removal to users who may edit the task's traits and
 * (b) warns + confirms before saving, since the trait vanishes from every
 * user's views until re-added.
 * This module isolates that decision so it can be unit-tested without rendering.
 */

export interface TraitRemovalDecision {
  /** Trait ids present originally but missing from the submitted selection. */
  removedTraitIds: string[];
  /** The actor removed a trait but is not allowed to — block the save. */
  blocked: boolean;
  /** Removal is permitted; show a confirmation dialog before saving. */
  needsConfirmation: boolean;
}

/**
 * Whether the user may edit a task's traits (select/append/remove) in the web
 * Edit Task form. Editors/admins always can; a collector only on a task they
 * created (the web steers collectors to mobile for field edits on others'
 * tasks). Viewers can't reach the form, and get false here for safety.
 */
export function canEditTaskTraits({
  userRole,
  isTaskCreator,
}: {
  userRole: string | null | undefined;
  isTaskCreator: boolean;
}): boolean {
  if (userRole === "editor" || userRole === "admin") return true;
  if (userRole === "collector") return isTaskCreator;
  return false;
}

/**
 * Normalize a dataCollectionConfig's `entryConfigRef` to its trait id.
 *
 * `entryConfigRef` is typed as a DocumentReference, but older/migrated data can
 * store it as a raw path string (the codebase handles both shapes elsewhere,
 * e.g. entryCollectionUtils / the server's entryConfigRefPath). Reading `.id`
 * directly returns undefined for a string ref, which would poison removal
 * detection and config preservation and could silently drop a config —
 * stranding (hiding) that trait's collected data.
 */
export function entryConfigIdOf(
  ref: { id?: string } | string | null | undefined
): string | undefined {
  if (!ref) return undefined;
  if (typeof ref === "string") return ref.split("/").pop() || undefined;
  return ref.id;
}

/** A current-task config reduced to what the save-merge needs. */
export interface ConfigSummary {
  id: string;
  /** True if the config is field-scoped (per-user `userVisibility` non-empty). */
  fieldScoped: boolean;
}

/**
 * Decide which CURRENT-task configs to keep verbatim when the edit form saves.
 *
 * The web global Edit Task form (matching iOS) edits only GLOBAL traits and
 * rebuilds them from the form; everything else on the task is preserved:
 *  - field-scoped (per-user) configs — hidden from the form, preserved verbatim;
 *  - globals the editor never saw (concurrently added while the form was open) —
 *    preserved so a stale form doesn't silently strand (hide) their data.
 * Dropped: a global the editor deliberately removed, and any config whose trait
 * is now built as a global (deduped — global visibility wins over per-user).
 *
 * Returns the ids (from `currentConfigs`) to keep, deduped, order-preserving.
 */
export function preservedConfigIds({
  initialGlobalIds,
  builtGlobalIds,
  currentConfigs,
}: {
  initialGlobalIds: string[];
  builtGlobalIds: string[];
  currentConfigs: ConfigSummary[];
}): string[] {
  const initial = new Set(initialGlobalIds);
  const built = new Set(builtGlobalIds);
  const result: string[] = [];
  const seen = new Set<string>();
  for (const config of currentConfigs) {
    if (built.has(config.id) || seen.has(config.id)) {
      continue; // promoted to / kept as global → already built (dedup, global wins)
    }
    if (config.fieldScoped) {
      seen.add(config.id);
      result.push(config.id); // hidden field-scoped trait → preserve verbatim
      continue;
    }
    if (!initial.has(config.id)) {
      seen.add(config.id);
      result.push(config.id); // global the editor never saw (concurrently added) → preserve
    }
    // else: a global the editor saw and removed → drop (intended removal)
  }
  return result;
}

/** Ids present in `originalTraitIds` but absent from `submittedTraitIds` (deduped, order-preserving). */
export function computeRemovedTraitIds(
  originalTraitIds: string[],
  submittedTraitIds: string[]
): string[] {
  const submitted = new Set(submittedTraitIds);
  const removed: string[] = [];
  const seen = new Set<string>();
  for (const id of originalTraitIds) {
    if (!submitted.has(id) && !seen.has(id)) {
      seen.add(id);
      removed.push(id);
    }
  }
  return removed;
}

/** Decide whether a trait removal is blocked or needs confirmation. */
export function decideTraitRemoval({
  originalTraitIds,
  submittedTraitIds,
  canRemoveTrait,
}: {
  originalTraitIds: string[];
  submittedTraitIds: string[];
  canRemoveTrait: boolean;
}): TraitRemovalDecision {
  const removedTraitIds = computeRemovedTraitIds(
    originalTraitIds,
    submittedTraitIds
  );
  const hasRemoval = removedTraitIds.length > 0;
  return {
    removedTraitIds,
    blocked: hasRemoval && !canRemoveTrait,
    needsConfirmation: hasRemoval && canRemoveTrait,
  };
}
